import { c as _c } from "react/compiler-runtime";
import { type CountryRecord, DEFAULT_COUNTRY } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { IdDI, type IdDocOutcome, type SubmitDocTypeResponse } from '@onefootprint/types';
import type { SupportedIdDocTypes } from '@onefootprint/types/src/data/id-doc-type';
import { useEffect, useState } from 'react';
import type { DeviceInfo } from '@/idv/hooks';
import { useDecryptUser } from '@/idv/queries';
import { getLogger, submitFingerprint } from '@/idv/utils';
import { isObject } from '@/idv/utils/type-guards';
import IdDocCountryAndTypeContainer from './components/id-doc-country-and-type-container';
import Loading from './components/loading';
const {
  logInfo,
  logWarn
} = getLogger({
  location: 'id-doc-country-and-type-container'
});
type IdDocCountryAndTypeProps = {
  authToken: string;
  selectedCountryCode?: Iso3166TwoDigitCountryCode;
  onSubmitDocType: (data: {
    type: `${SupportedIdDocTypes}`;
    country: Iso3166TwoDigitCountryCode;
    id: string;
  }) => void;
  device: DeviceInfo;
  supportedCountryAndDocTypes: Partial<Record<Iso3166TwoDigitCountryCode, SupportedIdDocTypes[]>>;
  sandboxOutcome?: IdDocOutcome;
  selectedDocType?: `${SupportedIdDocTypes}`;
  documentRequestId: string;
};
const IdDocCountryAndType = t0 => {
  const $ = _c(23);
  const {
    authToken,
    device,
    supportedCountryAndDocTypes,
    sandboxOutcome,
    selectedDocType,
    documentRequestId,
    onSubmitDocType,
    selectedCountryCode
  } = t0;
  const mutDecryptUser = useDecryptUser();
  const [userCountryCode, setUserCountryCode] = useState();
  let t1;
  if ($[0] !== supportedCountryAndDocTypes) {
    t1 = isObject(supportedCountryAndDocTypes) && Object.keys(supportedCountryAndDocTypes).length === 1;
    $[0] = supportedCountryAndDocTypes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasOneSupportedCountry = t1;
  const needToDecrypt = authToken && !hasOneSupportedCountry && !selectedCountryCode && !userCountryCode;
  const decryptionCompleted = mutDecryptUser.isSuccess || mutDecryptUser.isError;
  let t2;
  if ($[2] !== authToken || $[3] !== onSubmitDocType) {
    t2 = (data, country, docType) => {
      const {
        id
      } = data;
      submitFingerprint({
        authToken,
        path: "identity_document_start",
        documentId: id
      }).then(() => {
        logInfo(`submitFingerprint success for doc id: ${id}`);
      }).catch(error => {
        logWarn(`submitFingerprint error for doc id: ${id}`, error);
      });
      onSubmitDocType({
        type: docType,
        country: country.value ?? DEFAULT_COUNTRY.value,
        id
      });
    };
    $[2] = authToken;
    $[3] = onSubmitDocType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSubmitDocTypeSuccess = t2;
  let t3;
  if ($[5] !== authToken || $[6] !== mutDecryptUser || $[7] !== needToDecrypt) {
    t3 = () => {
      if (!needToDecrypt) {
        return;
      }
      if (mutDecryptUser.isIdle && !mutDecryptUser.isPending) {
        mutDecryptUser.mutate({
          authToken,
          fields: [IdDI.country]
        }, {
          onError: console.warn,
          onSuccess: res => {
            const countryCode = res[IdDI.country] as Iso3166TwoDigitCountryCode | undefined;
            if (countryCode) {
              setUserCountryCode(countryCode);
            }
          }
        });
      }
    };
    $[5] = authToken;
    $[6] = mutDecryptUser;
    $[7] = needToDecrypt;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== mutDecryptUser.isIdle || $[10] !== mutDecryptUser.isPending || $[11] !== needToDecrypt) {
    t4 = [needToDecrypt, mutDecryptUser.isIdle, mutDecryptUser.isPending];
    $[9] = mutDecryptUser.isIdle;
    $[10] = mutDecryptUser.isPending;
    $[11] = needToDecrypt;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  if (needToDecrypt && !decryptionCompleted) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Loading />;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t5;
  if ($[14] !== authToken || $[15] !== device || $[16] !== documentRequestId || $[17] !== handleSubmitDocTypeSuccess || $[18] !== sandboxOutcome || $[19] !== selectedDocType || $[20] !== supportedCountryAndDocTypes || $[21] !== userCountryCode) {
    t5 = <IdDocCountryAndTypeContainer onSubmitDocTypeSuccess={handleSubmitDocTypeSuccess} userCountryCode={userCountryCode} authToken={authToken} device={device} supportedCountryAndDocTypes={supportedCountryAndDocTypes} sandboxOutcome={sandboxOutcome} selectedDocType={selectedDocType} documentRequestId={documentRequestId} />;
    $[14] = authToken;
    $[15] = device;
    $[16] = documentRequestId;
    $[17] = handleSubmitDocTypeSuccess;
    $[18] = sandboxOutcome;
    $[19] = selectedDocType;
    $[20] = supportedCountryAndDocTypes;
    $[21] = userCountryCode;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};
export default IdDocCountryAndType;